import React, { useRef, useState, useEffect } from "react";

import { Box, Typography, Tooltip } from "@mui/material";
import { FiInfo } from "react-icons/fi";
import { format } from "date-fns";

import { CSVValueProps } from "../../../../../context/onBoardingContext";
import { useAppState } from "../../../../../context/StateContext";
import { UploadValueType, UploadCSVType } from "../../../../../util/enums";
import { isShipmentTracking } from "../../../../../util/validation";
import { UploadErrorModal } from "../../UploadErrorModal";
import { InputUpload } from "./InputUpload";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";
import { ExportCurve } from "iconsax-react";

type UploadFileValueProps = {
  title: string;
  description: string;
  information?: string;
  uploadType: UploadValueType;
  uploadFile?: CSVValueProps[];
  isUploaded?: boolean;
  columnType: UploadCSVType;
  onChangeFile?: (file: CSVValueProps[], columnType: UploadCSVType) => void;
  onRegisterInputRef?: (ref: HTMLInputElement | null) => void;
};

export const UploadFileValue = ({
  title,
  description,
  information,
  uploadType,
  uploadFile,
  isUploaded,
  onChangeFile,
  columnType,
  onRegisterInputRef,
}: UploadFileValueProps) => {
  const [isOpenUploadErrorModal, setIsOpenUploadErrorModal] = useState<boolean>(false);
  const [uploadedDataError, setUploadedDataError] = useState<string[]>([]);
  const [csvName, setCsvName] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAppState();

  // Register the input ref with parent on mount
  useEffect(() => {
    if (onRegisterInputRef) {
      onRegisterInputRef(inputRef.current);
    }
  }, [onRegisterInputRef]);

  const classes = useStyles({
    columnType,
    hasUploadFile: (uploadFile && uploadFile.length > 0) || isUploaded,
    isShipment: isShipmentTracking(currentUser?.company),
  });

  const isShipmentTrackingBoolean = isShipmentTracking(currentUser?.company);
  // Show file info only when the file was successfully uploaded (persisted) or when rows exist.
  // Do not treat a merely-selected file (csvName only) as an uploaded file.
  const hasUploadedFile = (uploadFile && uploadFile.length > 0) || isUploaded;

  // Format the date for display (matches Figma: "Aug 10, 2025 at 10:30 am")
  const getFormattedDate = () => {
    try {
      return format(new Date(), "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return "Recently uploaded";
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!isShipmentTrackingBoolean && (
        <Box sx={{ mb: hasUploadedFile ? 1.5 : 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: hasUploadedFile ? 1 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flex: 1 }}>
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: colors.brandBlue500,
                  mt: "6px",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: hasUploadedFile ? 0.5 : 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "16px",
                      color: colors.gray600,
                      lineHeight: "18px",
                    }}
                  >
                    {title}
                  </Typography>

                  {uploadType !== UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST &&
                    information &&
                    columnType === UploadCSVType.INVENTORY_LIST && (
                      <Tooltip title={information} placement="top">
                        <Box
                          sx={{ display: "flex", alignItems: "center", cursor: "pointer", ml: 0.5 }}
                        >
                          <FiInfo size={16} color="#6B7280" />
                        </Box>
                      </Tooltip>
                    )}
                </Box>

                {/* Show uploaded file info - matches Figma design. Only show when upload succeeded (uploadFile has actual data). */}
                {uploadFile && uploadFile.length > 0 && csvName && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontSize: "14px",
                        color: "#101828",
                        mb: 0.5,
                        lineHeight: "20px",
                      }}
                    >
                      {csvName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "Manrope",
                        fontWeight: 400,
                        fontSize: "12px",
                        color: "#6B7280",
                        lineHeight: "16px",
                      }}
                    >
                      By {currentUser?.name || "User"} {getFormattedDate()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {uploadType !== UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST && (
              <Box
                onClick={() => {
                  if (inputRef) {
                    inputRef.current?.click();
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                  border: `1px solid ${colors.brandBlue500}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  ml: 1,
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: "rgba(0, 85, 164, 0.04)",
                    borderColor: colors.brandBlue500,
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ExportCurve size={20} color={colors.brandBlue500} />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {uploadType !== UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST && (
        <Box sx={{ display: "none" }}>
          <InputUpload
            inputRef={inputRef}
            uploadFile={uploadFile}
            columnType={columnType}
            isUploaded={isUploaded}
            onChangeCsvName={(newCsvName) => setCsvName(newCsvName)}
            onChangeUploadedError={(newUploadedError) => {
              setUploadedDataError(newUploadedError);
              // Clear csvName if validation errors exist so incorrect filename isn't displayed
              if (newUploadedError && newUploadedError.length > 0) {
                setCsvName("");
              }
            }}
            onChangeFile={(file) => {
              if (onChangeFile) onChangeFile(file, columnType);
            }}
            onOpenErrorModal={() => setIsOpenUploadErrorModal(true)}
          />
        </Box>
      )}

      <UploadErrorModal
        isOpen={isOpenUploadErrorModal}
        uploadDataError={uploadedDataError}
        fileName={csvName}
        onClose={() => setIsOpenUploadErrorModal(false)}
        onEditFile={() => {
          if (inputRef && columnType !== UploadCSVType.INVENTORY_LIST) {
            inputRef.current?.click();
            setIsOpenUploadErrorModal(false);
          }
        }}
      />
    </Box>
  );
};
